import React, { FC, useState, useEffect } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import { useRouter } from 'next/router'
import { convertQueryParamsAndRemoveEmptyGetParameters } from '@/core/toStringHelper'
import { FilterQueryItem } from '@/types/filterQueryItem'
import { routerPushShallow } from '@/providers/filterProvider/filtersProvider'
import { validFor } from '@/theme/validFor'
import { ArrowIconBreadcrumbnDefault } from '../atoms/icons/ArrowIconBreadcrumb'
import useTranslation from 'next-translate/useTranslation'
import styled, { useTheme } from 'styled-components'
import { ButtonBaseHoverNew, ButtonBaseNew } from '../atoms/button'
import useDevice from 'hooks/useDevice'

type PagerProps = {
  numberOfPages: number
  currentPageIndex: number
  onClick: (page: number) => void
}

const PagerNew: FC<PagerProps> = ({
  numberOfPages,
  currentPageIndex,
  onClick
}) => {
  const theme = useTheme()
  const { isDesktop } = useDevice()
  const router = useRouter()
  const { t } = useTranslation('common')

  const [pendingPageIndex, setPendingPageIndex] = useState<number | null>(null)

  const effectivePageIndex = pendingPageIndex ?? currentPageIndex

  useEffect(() => {
    if (pendingPageIndex !== null && pendingPageIndex === currentPageIndex) {
      setPendingPageIndex(null)
    }
  }, [currentPageIndex, pendingPageIndex])

  if (numberOfPages !== 0 && numberOfPages < 2) {
    return null
  }

  const allPages = Array.from({ length: Math.ceil(numberOfPages) })
  let previousValue: string

  const countPageBox = 1

  const pagesToDisplay: Array<{
    value: string
    isNumber: boolean
    isSelected?: boolean
    index?: number
  }> = allPages
    .map((value, index) => {
      if (index < 0) {
        return null
      }

      const pageNumber = index + 1
      const currentPage = effectivePageIndex
      const isFirst = index === 0
      const isLast = index === allPages.length - 1
      const isInRange = Math.abs(index - (currentPage - 1)) <= countPageBox

      if (isFirst || isLast || isInRange) {
        const pageNumberStr = pageNumber.toString()
        previousValue = pageNumberStr
        return {
          value: pageNumberStr,
          isNumber: true,
          isSelected: pageNumberStr === effectivePageIndex.toString()
        }
      }

      if (previousValue === '...') {
        return null
      }

      previousValue = '...'
      return { value: '...', isNumber: false, isSelected: false }
    })
    .filter((dict) => dict)

  const pageClick = (page: string) => {
    const pageNum = parseInt(page)
    setPendingPageIndex(pageNum)

    if (page === (router.query.page as string)) {
      onClick(pageNum)
      return
    }
    router.query.page = page

    const stringQuery = convertQueryParamsAndRemoveEmptyGetParameters({
      query: router.query,
      allowOnly: [...Object.values(FilterQueryItem), 'page']
    })
    const basePath = router.asPath.split('?')[0]
    const path = `${basePath}${stringQuery ? '?' : ''}${stringQuery}`
    routerPushShallow(path, router, router.query)
  }

  const handlePrevious = () => {
    if (currentPageIndex > 1) {
      pageClick((currentPageIndex - 1).toString())
    }
  }

  const handleNext = () => {
    if (currentPageIndex < numberOfPages) {
      pageClick((currentPageIndex + 1).toString())
    }
  }

  if (pagesToDisplay.length === 0) {
    return null
  }

  return (
    <Wrapper>
      {isDesktop ? (
        <>
          <NavigationButton
            onClick={handlePrevious}
            disabled={currentPageIndex <= 1}
          >
            <ArrowIconBreadcrumbnDefault
              width={15}
              height={15}
              color={currentPageIndex <= 1 ? '#999' : theme.colors.onBackground}
            />
            {t('Pager.back')}
          </NavigationButton>

          {pagesToDisplay.map(({ value, isNumber, isSelected }, index) =>
            isNumber ? (
              <PageBox
                key={`page-${value}-${isSelected ? 'selected' : 'unselected'}`}
                isSelected={isSelected}
                onClick={() => pageClick(value)}
              >
                {parseInt(value)}
              </PageBox>
            ) : (
              <DotsBox key={`dots-${index}`}>{value}</DotsBox>
            )
          )}

          <NavigationButton
            onClick={handleNext}
            disabled={currentPageIndex >= numberOfPages}
          >
            {t('Pager.next')}
            <ArrowIconBreadcrumbnDefault
              width={15}
              height={15}
              color={
                currentPageIndex >= numberOfPages
                  ? '#999'
                  : theme.colors.onBackground
              }
              style={{
                transform: `rotate(${180}deg)`,
                transformOrigin: 'center'
              }}
            />
          </NavigationButton>
        </>
      ) : (
        <>
          <NavigationButton
            onClick={handlePrevious}
            disabled={currentPageIndex <= 1}
          >
            <ArrowIconBreadcrumbnDefault
              width={20}
              height={20}
              color={currentPageIndex <= 1 ? '#999' : theme.colors.onBackground}
            />
          </NavigationButton>

          <PageBoxMobile>
            {t('Pager.page')} {currentPageIndex} {t('Pager.from')}{' '}
            {numberOfPages}
          </PageBoxMobile>

          <NavigationButton
            onClick={handleNext}
            disabled={currentPageIndex >= numberOfPages}
          >
            <ArrowIconBreadcrumbnDefault
              width={20}
              height={20}
              color={
                currentPageIndex >= numberOfPages
                  ? '#999'
                  : theme.colors.onBackground
              }
              style={{
                transform: `rotate(${180}deg)`,
                transformOrigin: 'center'
              }}
            />
          </NavigationButton>
        </>
      )}
    </Wrapper>
  )
}

const NavigationButton = styled.button`
  ${ButtonBaseHoverNew};
  min-height: 45px;
  padding: 12px 16px 12px 11px;
  gap: 8px;
  display: flex;
  align-items: center;

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  ${validFor.mobile`
   padding: 6px 3px;
    min-height: 0;
  `}
`

const PageBoxMobile = styled(Box)`
  margin: 0 15px;
`

const PageBox = styled(Box).withConfig({
  shouldForwardProp: (prop) => prop !== 'isSelected'
})`
  ${ButtonBaseHoverNew}
  min-width: 9px;
  padding: 12px;
  color: ${(props) =>
    props.isSelected
      ? props.theme.colors.background
      : props.theme.colors.buttonColorNew};
  background-color: ${(props) =>
    props.isSelected
      ? props.theme.colors.onBackground
      : props.theme.colors.background};

  cursor: ${(props) =>
    props.isSelected ? 'not-allowed!important' : 'pointer'};
`

const DotsBox = styled(Box)`
  ${ButtonBaseNew};
  padding: 12px;
`

const Wrapper = styled(Flex)`
  min-height: 32px;
  column-gap: 8px;
  margin-top: 64px;
  margin-bottom: 24px;
  justify-content: center;
  align-items: center;
  ${validFor.mobile`
    column-gap: 4px;
    margin-top: 64px;
    margin-bottom: 8px;
    justify-content: center;
  `}
`

export default PagerNew
